import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Breadcrumb from "../components/Breadcrumb";
import { AddContact } from "../API/ContactAPI";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { PuffLoader } from "react-spinners";

// Validation Schema
const ContactSchema = Yup.object().shape({
  Name: Yup.string().required("Name is required"),
  Email: Yup.string().email("Invalid email").required("Email is required"),
  Phone: Yup.string()
    .matches(/^[0-9]+$/, "Phone must be only digits")
    .required("Phone number is required"),
  Message: Yup.string().required("Message is required"),
});

// Initial Values
const initialValues = {
  Name: "",
  CCODE: "",
  Email: "",
  Phone: "",
  Message: "",
};

const Contact = () => {
  const [isFormLoading, setIsFormLoading] = useState(false);

  // Submit Handler
  const handleSubmit = async (values, { resetForm }) => {
    setIsFormLoading(true);
    try {
      const result = await AddContact(values);
      if (result.status === true) {
        resetForm();
      }
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setIsFormLoading(false);
    }
  };

  return (
    <>
      {isFormLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PuffLoader color={"#83442B"} size={60} />
        </div>
      )}
      <Breadcrumb title="Contact" path="Contact" />

      <section className="py-8 md:py-12 container mx-auto px-4">
        <div className="loader-line"></div>
        <h2 className="text-2xl md:text-4xl font-semibold text-darktext mb-2 mt-3">
          Get in Touch With Us
        </h2>
        <p className="text-contentcolor mb-6 md:mb-8 text-sm md:text-base">
          We're here to answer your questions and help you every step of the
          way.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
          {/* Contact Form */}
          <div className="lg:col-span-2 border bg-white shadow-md rounded-md p-4 md:p-6">
            <Formik
              initialValues={initialValues}
              validationSchema={ContactSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, handleReset }) => (
                <Form className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="Name" className="text-darktext block mb-1 text-sm md:text-base font-medium">Name</label>
                    <Field
                      type="text"
                      name="Name"
                      placeholder="Enter your name"
                      className="w-full border rounded-md px-3 py-2.5 md:py-3 focus:outline-none focus:ring-1 focus:ring-theme2 text-sm md:text-base"
                    />
                    <ErrorMessage
                      name="Name"
                      component="div"
                      className="text-red-500 text-xs md:text-sm mt-1"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="Email" className="text-darktext block mb-1 text-sm md:text-base font-medium">Email</label>
                    <Field
                      type="email"
                      name="Email"
                      placeholder="Enter your email"
                      className="w-full border rounded-md px-3 py-2.5 md:py-3 focus:outline-none focus:ring-1 focus:ring-theme2 text-sm md:text-base"
                    />
                    <ErrorMessage
                      name="Email"
                      component="div"
                      className="text-red-500 text-xs md:text-sm mt-1"
                    />
                  </div>

                  {/* Phone with Country Code */}
                  <div className="md:col-span-2">
                    <label htmlFor="Phone" className="text-darktext block mb-1 text-sm md:text-base font-medium">Phone Number</label>
                    <PhoneInput
                      country={"in"}
                      value={values.CCODE + values.Phone}
                      onChange={(value, countryData) => {
                        setFieldValue("CCODE", "+" + countryData.dialCode);
                        setFieldValue(
                          "Phone",
                          value.replace(countryData.dialCode, "")
                        );
                      }}
                      inputClass="!w-full !py-2.5 md:!py-3 !border !rounded-md !focus:outline-none !focus:ring-1 !focus:ring-theme2 !text-sm md:!text-base"
                    />
                    <ErrorMessage
                      name="Phone"
                      component="div"
                      className="text-red-500 text-xs md:text-sm mt-1"
                    />
                  </div>

                  {/* Message */}
                  <div className="md:col-span-2">
                    <label htmlFor="Message" className="text-darktext block mb-1 text-sm md:text-base font-medium">Message</label>
                    <Field
                      as="textarea"
                      rows="4"
                      name="Message"
                      placeholder="Hi there, I would like to..."
                      className="w-full border rounded-md px-3 py-2.5 md:py-3 focus:outline-none focus:ring-1 focus:ring-theme2 text-sm md:text-base resize-none"
                    />
                    <ErrorMessage
                      name="Message"
                      component="div"
                      className="text-red-500 text-xs md:text-sm mt-1"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="md:col-span-2 flex flex-col sm:flex-row justify-end gap-3 md:gap-4 mt-2">
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={isFormLoading}
                      className="px-4 py-2 md:px-6 md:py-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 text-sm md:text-base font-medium"
                    >
                      CANCEL
                    </button>
                    <button
                      type="submit"
                      disabled={isFormLoading}
                      className="px-4 py-2 md:px-6 md:py-2 rounded-full bg-theme2 hover:bg-black transition text-white disabled:opacity-50 text-sm md:text-base font-medium"
                    >
                      {isFormLoading ? "Submitting..." : "SUBMIT"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          {/* Right Side Info Boxes */}
          <div className="space-y-4 md:space-y-6">
            <div className="p-4 md:p-6 lg:p-8 border rounded-xl bg-white shadow-md">
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-darktext mb-2">
                Contact Support
              </h3>
              <p className="text-contentcolor text-sm md:text-base mb-3">
                Need immediate assistance? Reach out to us directly.
              </p>
              <a 
                href="mailto:support@rotiey.com" 
                className="inline-flex items-center text-theme2 hover:text-black transition-colors text-sm md:text-base font-medium"
              >
                📧 support@rotiey.com
              </a>
            </div>

             

            <div className="p-4 md:p-6 lg:p-8 border rounded-xl bg-white shadow-sm">
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-darktext mb-2">
                Issue with your order?
              </h3>
              <p className="text-contentcolor text-sm md:text-base">
                Click on the 'Support' or 'ordering help' section in your app
                to connect to our customer support team.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;